import { useState } from "react";

// Real India outline path scaled to 500x565 viewBox
// Source: world-map-country-shapes (ISO 3166-1 IN), transformed to local coords
const INDIA_PATH = "M205.2 109.0L177.7 94.8L157.7 81.9L147.4 59.3L160.6 62.2L158.7 51.6L149.0 41.0L146.5 24.2L121.9 0.0L110.0 17.4L91.6 20.6L64.2 15.5L58.1 24.5L68.4 42.6L77.7 56.4L93.9 66.4L81.9 78.4L85.2 92.9L72.6 113.2L65.8 134.2L51.3 155.8L30.6 154.1L14.8 175.4L27.7 184.8L31.9 200.6L43.2 210.9L49.0 228.6L10.3 228.6L-0.0 242.2L22.9 259.6L29.0 267.7L21.3 275.1L47.1 299.9L60.0 302.5L84.5 290.2L90.0 309.3L92.6 334.4L100.6 360.5L112.3 400.2L131.0 428.6L135.2 441.2L141.6 467.0L152.6 486.6L159.7 496.3L167.7 516.9L177.7 545.7L195.5 565.0L202.6 559.2L208.1 545.0L224.2 539.2L218.4 532.4L225.5 516.9L234.8 516.0L232.6 481.2L238.7 461.5L236.5 444.4L230.3 417.9L234.2 402.1L242.3 401.2L257.7 393.8L266.1 388.6L265.2 379.2L281.3 365.7L293.2 352.8L310.3 328.6L334.2 315.1L341.9 302.8L339.0 287.3L360.3 283.1L372.3 283.5L373.9 275.7L368.7 259.0L360.3 243.5L361.6 231.2L349.7 225.7L352.3 218.3L362.3 210.6L347.4 199.6L351.3 185.8L366.8 194.5L375.5 195.8L379.4 209.9L396.8 212.8L412.9 212.5L423.9 216.1L418.7 233.2L411.0 234.4L407.4 246.1L418.7 256.7L419.4 243.8L424.2 243.5L438.7 276.1L446.5 271.2L443.5 262.5L446.5 255.7L443.5 234.4L458.4 239.0L463.2 222.2L462.3 212.2L469.0 194.8L466.1 183.2L485.8 169.0L499.0 172.5L494.8 160.0L500.0 156.1L497.1 148.3L477.4 145.4L481.3 136.7L470.0 124.2L459.7 132.5L443.9 127.7L426.8 140.6L414.2 156.1L400.6 158.7L409.4 165.1L410.6 177.7L396.5 178.3L381.3 177.0L371.0 180.3L353.2 172.2L352.3 168.3L347.4 151.9L337.7 156.4L338.1 165.1L342.9 178.3L342.6 186.4L327.7 186.7L305.8 181.9L291.9 179.9L279.7 169.6L255.2 166.7L230.3 155.4L211.6 145.4L193.2 137.4L196.1 118.4L205.2 109.0Z";

const VW = 500;
const VH = 565;
const COLS = 55;
const ROWS = 62;

function parsePathToSegments(d: string) {
  const segments: number[][] = [];
  const tokens = d.match(/[MLZ]|[-+]?\d*\.?\d+/g) || [];
  let i = 0, startX = 0, startY = 0, cx = 0, cy = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t === "M") {
      cx = parseFloat(tokens[++i]);
      cy = parseFloat(tokens[++i]);
      startX = cx; startY = cy;
    } else if (t === "L") {
      const nx = parseFloat(tokens[++i]);
      const ny = parseFloat(tokens[++i]);
      segments.push([cx, cy, nx, ny]);
      cx = nx; cy = ny;
    } else if (t === "Z") {
      segments.push([cx, cy, startX, startY]);
      cx = startX; cy = startY;
    }
    i++;
  }
  return segments;
}

function pointInSegments(px: number, py: number, segs: number[][]) {
  let inside = false;
  for (const [x1, y1, x2, y2] of segs) {
    const intersect = y1 > py !== y2 > py && px < ((x2 - x1) * (py - y1)) / (y2 - y1) + x1;
    if (intersect) inside = !inside;
  }
  return inside;
}

const segments = parsePathToSegments(INDIA_PATH);

interface Dot { col: number; row: number; px: number; py: number; }
const dots: Dot[] = [];
for (let row = 0; row < ROWS; row++) {
  for (let col = 0; col < COLS; col++) {
    const px = (col + 0.5) / COLS * VW;
    const py = (row + 0.5) / ROWS * VH;
    if (pointInSegments(px, py, segments)) {
      dots.push({ col, row, px, py });
    }
  }
}

// Tamil Nadu region bounds in path coordinates
function isTamilNadu(px: number, py: number) {
  return px > 138 && px < 235 && py > 448 && py < 565;
}


// Locations in path coordinate space (Coimbatore ~76.9°E, 11.0°N)
const locations = [
  {
    id: "coimbatore",
    name: "Coimbatore",
    address: "No 1209/2, Sai Imperia Grand, Mettupalayam Road, Saibaba Colony, Coimbatore - 641011",
    px: 165, py: 487,
  },
  {
    id: "pollachi",
    name: "Pollachi",
    address: "83/14 1st Floor, Angel Broking, Vallar Street, Bus Stand Back Side, Pollachi - 642001",
    px: 172, py: 500,
  },
  {
    id: "udumalpet",
    name: "Udumalpet",
    address: "8/9 2nd Floor, Angel Broking, 100 Feet Road, Abirami Nagar, Udumalpet - 642126",
    px: 185, py: 493,
  },
];

interface Location { id: string; name: string; address: string; px: number; py: number; }

function Marker({ loc, isActive, onEnter, onLeave }: { loc: Location; isActive: boolean; onEnter: () => void; onLeave: () => void; }) {
  const left = `${(loc.px / VW) * 100}%`;
  const top = `${(loc.py / VH) * 100}%`;

  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onEnter}
      style={{ position: "absolute", left, top, transform: "translate(-50%, -50%)", cursor: "pointer", zIndex: isActive ? 20 : 10 }}
    >
      {[0, 0.55, 1.1].map((delay, i) => (
        <div key={i} className="absolute w-8 h-8 -left-2.5 -top-2.5 rounded-full border-[1.5px] border-[var(--color-accent)]/40 animate-ripple pointer-events-none" style={{ animationDelay: `${delay}s` }} />
      ))}
      <div className="absolute w-6 h-6 -left-1.5 -top-1.5 rounded-full bg-[radial-gradient(circle,rgba(95,156,76,0.4)_0%,transparent_70%)] blur-[4px] animate-breathe pointer-events-none" />
      <div className={`w-3 h-3 rounded-full bg-[radial-gradient(circle_at_35%_30%,#164c88,#164c88)] border-[1.5px] border-white/60 transition-transform duration-200 relative z-[2] ${isActive ? "scale-[1.8]" : "scale-100"
        }`} />
      {isActive && (
        <div className="absolute bottom-[calc(100%+10px)] left-1/2 -translate-x-1/2 bg-[var(--color-accent)] text-black px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap shadow-[0_4px_20px_rgba(95,156,76,0.5)] z-[30] tracking-[0.4px]">
          {loc.name}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-[var(--color-accent)] [clip-path:polygon(0_0,100%_0,50%_100%)]" />
        </div>
      )}
    </div>
  );
}

export function MapAnimation() {
  return (
    <div className="relative w-full aspect-[500/565] max-w-[200px] mx-auto opacity-20 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-700">
      <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full h-full block">
        <defs>
          <filter id="tnGlowFooter">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <path d={INDIA_PATH} fill="rgba(95,156,76,0.03)" stroke="rgba(95,156,76,0.18)" strokeWidth="1.5" />
        {dots.map(({ col, row, px, py }) => {
          const isTN = isTamilNadu(px, py);
          return (
            <circle
              key={`${col}-${row}`}
              cx={px} cy={py}
              r={isTN ? 4 : 3.4}
              className={isTN
                ? "fill-[var(--color-accent)]/80"
                : "fill-[var(--color-text)] opacity-[0.08]"}
              filter={isTN ? "url(#tnGlowFooter)" : undefined}
            />
          );
        })}
      </svg>
    </div>
  );
}

export default function AcademyMap() {
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <section className="bg-transparent min-h-[60vh] flex items-center justify-center py-12 px-6">
      <div className="w-full max-w-[900px]">

        <div className="text-center mb-[52px]">
          <h2 className="text-[clamp(28px,5vw,48px)] font-extrabold m-0 tracking-[-2px] text-[var(--color-text)]">
            Our <span className="text-[var(--color-accent)]">Presence</span>
          </h2>
          <p className="mt-2.5 text-sm opacity-60 text-[var(--color-text)] max-w-[500px] mx-auto">
            Our offices across Tamil Nadu helping traders grow with confidence.
          </p>
        </div>

        <div className="flex gap-12 items-center flex-wrap justify-center">

          {/* India Dot Map */}
          <div className="relative w-[min(300px,80vw)] shrink-0 aspect-[500/565]">
            <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full h-full block">
              <defs>
                <filter id="tnGlow">
                  <feGaussianBlur stdDeviation="1.5" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>

              {/* Subtle border outline */}
              <path d={INDIA_PATH} fill="rgba(95,156,76,0.03)" stroke="rgba(95,156,76,0.18)" strokeWidth="1.5" />

              {/* All dots */}
              {dots.map(({ col, row, px, py }) => {
                const isTN = isTamilNadu(px, py);
                return (
                  <circle
                    key={`${col}-${row}`}
                    cx={px} cy={py}
                    r={isTN ? 4 : 3.4}
                    className={isTN
                      ? "fill-[var(--color-accent)]/80"
                      : "fill-[var(--color-text)] opacity-[0.08]"}
                    filter={isTN ? "url(#tnGlow)" : undefined}
                  />
                );
              })}
            </svg>

            <div className="absolute inset-0">
              {locations.map((loc) => (
                <Marker
                  key={loc.id} loc={loc}
                  isActive={activeId === loc.id}
                  onEnter={() => setActiveId(loc.id)}
                  onLeave={() => setActiveId(null)}
                />
              ))}
            </div>
          </div>

          {/* Cards */}
          <div className="flex flex-col gap-3.5 flex-1 min-w-[240px] max-w-[380px]">
            {locations.map((loc) => (
              <div
                key={loc.id}
                onMouseEnter={() => setActiveId(loc.id)}
                onMouseLeave={() => setActiveId(null)}
                className={`rounded-2xl p-4 cursor-pointer transition-all duration-250 border ${activeId === loc.id
                    ? "bg-[rgba(95,156,76,0.1)] border-[rgba(95,156,76,0.4)] translate-x-1"
                    : "bg-[var(--glass-surface)] border-[var(--glass-border)] hover:border-[rgba(95,156,76,0.3)]"
                  }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full bg-[var(--color-accent)] flex-shrink-0 ${activeId === loc.id ? "shadow-[0_0_8px_rgba(95,156,76,0.8)]" : ""
                    }`} />
                  <h3 className="text-[var(--color-accent)] font-bold text-sm">{loc.name}</h3>
                </div>
                <p className="text-[var(--color-text)] opacity-50 text-xs leading-relaxed">{loc.address}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

    </section>
  );
}
